import useGeoModelAccessQuery from "components/org-settings/hooks/useGeoModelAccessQuery";

import useGeoModelEntitlement from "./useGeoModelEntitlement";

/**
 * Whether an org-level geo administration surface should be available: the org
 * is entitled to geo models, or already owns some.
 *
 * Named for what it answers — it is deliberately true for an org with zero geo
 * models, because an entitled org must be able to administer access and
 * defaults before its first upload. See {@link useGeoModelEntitlement} for why
 * a has-models check cannot gate these surfaces.
 *
 * Reads the unfiltered admin list, so an admin who has granted themselves
 * access to nothing still sees the surface. That query renders admin-only
 * surfaces here; anything rendering for every role must use
 * {@link useGeoModelEntitlement} directly instead.
 *
 * Known and accepted: an org holding only map layer groups (from
 * `geo-map-svc`, which this never queries) reads false once the upload beta is
 * revoked. Map layers can only be created through that beta, so that is a
 * disabled org rather than a reachable gap.
 *
 * @param enabled pass an explicit boolean, not a possibly-undefined role flag:
 * `undefined` counts as an omitted argument and would silently re-enable the
 * fetch for the very users the caller meant to exclude.
 */
export default function useCanUseGeoModels(enabled = true): boolean {
  const isEntitled = useGeoModelEntitlement();
  const { geoModels, isError } = useGeoModelAccessQuery(enabled);

  // A failed lookup must not read as "this org has no geo models": the query
  // swallows its own errors and falls back to [], which would silently hide
  // administration from exactly the orgs this exists to serve. Safe here
  // because every caller is already admin- or power-user-gated.
  return isEntitled || isError || geoModels.length > 0;
}
